// src/components/settings/settings-controller.ts
import { LitElement, html, css, PropertyValueMap } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

interface Settings {
    theme: 'dark' | 'light' | 'system'
    // Future settings can be added here
}

@customElement('settings-controller')
export class SettingsController extends LitElement {
    @property({ type: Object })
    settings: Settings = {
        theme: 'system',
    }

    @state()
    private systemThemePreference: 'dark' | 'light' = 'dark'

    static styles = css`
        :host {
            display: none; /* Hidden element */
        }
    `

    constructor() {
        super()
        // Load saved settings
        const savedSettings = localStorage.getItem('app-settings')
        if (savedSettings) {
            try {
                this.settings = JSON.parse(savedSettings)
            } catch (e) {
                console.error('Failed to parse saved settings')
            }
        }

        // Detect system preference
        this.detectSystemPreference()
        // Listen for system changes
        window
            .matchMedia('(prefers-color-scheme: dark)')
            .addEventListener('change', () => this.detectSystemPreference())
    }

    connectedCallback() {
        super.connectedCallback()
        this.updateTheme()
    }

    updated(changedProperties: PropertyValueMap<any> | Map<string, unknown>) {
        if (changedProperties.has('settings')) {
            // Save settings when they change
            localStorage.setItem('app-settings', JSON.stringify(this.settings))
            this.updateTheme()
        }
    }

    private detectSystemPreference() {
        this.systemThemePreference = window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light'
        if (this.settings.theme === 'system') {
            this.updateTheme()
        }
    }

    private updateTheme() {
        const activeTheme =
            this.settings.theme === 'system' ? this.systemThemePreference : this.settings.theme

        // Remove existing theme classes
        document.documentElement.classList.remove('theme-dark', 'theme-light')
        // Add appropriate theme class
        document.documentElement.classList.add(`theme-${activeTheme}`)
    }

    updateSetting(key: string, value: any) {
        this.settings = {
            ...this.settings,
            [key]: value,
        }
    }

    render() {
        return html``
    }
}

// Helper functions
export function getSettingsController() {
    return document.querySelector('settings-controller') as SettingsController
}

export function updateThemeSetting(theme: any) {
    const controller = getSettingsController()
    if (controller) {
        controller.updateSetting('theme', theme)
    }
}
